import { formatDate, FormatDateType, isDateInFuture } from '@/helpers/date';
import getDomainFromUrl from '@/helpers/getDomainFromUrl';
import { getSafeInnerHTMLProp } from '@/helpers/getSafeInnerHTML';
import useAuthentication from '@/hooks/useAuthentication/useAuthentication';
import useBenefits from '@/hooks/useBenefits/useBenefits';
import { HeroSize } from '@uikit/composites/HeroArea/consts';
import HeroArea from '@uikit/composites/HeroArea/HeroArea';
import React, { useEffect } from 'react';
import Accordion from '../Accordion/Accordion';
import AppModal from '../AppModal/AppModal';
import BenefitsDealCard from '../BenefitsDealCard/BenefitsDealCard';
import BenefitsHint from '../BenefitsHint/BenefitsHint';
import BenefitsSimilarDeals from '../BenefitsSimilarDeals/BenefitsSimilarDeals';
import BenefitsStagebox from '../BenefitsStagebox/BenefitsStagebox';
import Button from '../Button/Button';
import { ButtonAlign, ShowIcon } from '../Button/consts';
import { ClickableAction, ClickableTarget } from '../Clickable/consts';
import Container from '../Container/Container';
import ErrorModal from '../ErrorModal/ErrorModal';
import {
  HeadlineAlign,
  HeadlineColor,
  HeadlineLevel,
} from '../Headline/consts';
import Headline from '../Headline/Headline';
import PromocodeButton from '../PromocodeButton/PromocodeButton';
import Section from '../Section/Section';
import ShowcaseElement from '../ShowcaseElement/ShowcaseElement';
import Spacer from '../Spacer/Spacer';
import TwoColumnContainer from '../TwoColumnContainer/TwoColumnContainer';
import UnorderedList from '../UnorderedList/UnorderedList';
import UnorderedListItem from '../UnorderedList/UnorderedListItem';
import styles from './BenefitsPageContent.module.scss';
import type { BenefitsPageContentProps } from './interfaces';

const BenefitsPageContent = ({
  appOnly,
  couponType,
  dealId,
  details,
  endDate,
  faqItems,
  footNote,
  headline,
  heroImage,
  link,
  redeemedButtonText,
  redeemStepItems,
  showcaseDescription,
  showcaseImage,
  showcaseTitle,
  similarDeals,
  stageVariant,
  startDate,
  status,
  subtitle,
  teaserDescription,
  teaserHeadline,
  urlLabel,
  vimeoUrl,
}: BenefitsPageContentProps) => {
  const hasFutureStartDate = isDateInFuture(startDate);
  const { getCouponFromDeal, userCoupons } = useBenefits();
  const currentDealCoupon = userCoupons[dealId];
  const [couponCode, setCouponCode] = React.useState('🙈🙈🙈');
  const [buttonState, setButtonState] = React.useState<
    'initial' | 'loading' | 'activated' | 'disabled'
  >('initial');
  const { isAuthenticated, openAuthenticationModal } = useAuthentication();
  const [isNotAvailable, setIsNotAvailable] = React.useState(false);
  const [isNotEligible, setIsNotEligible] = React.useState(false);
  const [shouldShowAppModal, setShouldShowAppModal] = React.useState(false);

  const shouldShowLink = link && couponType !== 'link';

  const handleCouponRequest = async () => {
    if (!isAuthenticated) {
      openAuthenticationModal({
        modalReason: 'benefits',
        loginCallback: handleFetchCoupon,
      });

      return;
    }

    await handleFetchCoupon();
  };

  const handleFetchCoupon = async () => {
    try {
      const coupon = await getCouponFromDeal(dealId);
      setCouponCode(coupon);
      setButtonState('activated');
    } catch (error) {
      if (error instanceof Response) {
        setButtonState('initial');

        switch (error.status) {
          case 401:
            openAuthenticationModal({
              modalReason: 'benefits',
              loginCallback: handleFetchCoupon,
            });
            break;

          case 403:
            setIsNotEligible(true);
            break;

          case 404:
          default:
            setIsNotAvailable(true);
            break;
        }
      }
    }
  };

  const getButtonSlot = () => {
    if (status === 'inactive') {
      return (
        <BenefitsHint>Dieser Gutschein ist leider vergriffen.</BenefitsHint>
      );
    }

    if (hasFutureStartDate) {
      return <BenefitsHint>Diese Aktion ist bald verfügbar.</BenefitsHint>;
    }

    if (couponType === 'link' && link) {
      return (
        <Button
          actionType={ClickableAction.Linkextern}
          icon={'action/external-link'}
          showIcon={ShowIcon.ShowIcon}
          linkextern={link}
          target={ClickableTarget.Blank}
        >
          Aktion aufrufen
        </Button>
      );
    }

    if (appOnly) {
      return (
        <Button
          actionType={ClickableAction.Custom}
          onClick={() => setShouldShowAppModal(true)}
          icon={'action/external-link'}
          showIcon={ShowIcon.ShowIcon}
        >
          In der App abrufen
        </Button>
      );
    }

    return (
      <>
        <PromocodeButton
          label={'Gutschein abrufen'}
          onClick={handleCouponRequest}
          revealedLabel={couponCode}
          showCopyIcon={true}
          size="large"
          state={buttonState}
        />
        {redeemedButtonText && (
          <span className={styles.redeemedButtonText}>
            {redeemedButtonText}
          </span>
        )}
      </>
    );
  };

  useEffect(() => {
    if (currentDealCoupon) {
      setCouponCode(currentDealCoupon);
      setButtonState('activated');
    }
  }, [currentDealCoupon]);

  return (
    <React.Fragment>
      <div className={styles.base}>
        <div className={styles.heroWrapper}>
          <HeroArea
            hasAspectRatioFallback={true}
            image={heroImage}
            size={HeroSize.Large}
            skewedBottom={true}
            stageBackground={stageVariant}
            vimeoUrl={vimeoUrl}
          >
            <BenefitsStagebox buttonSlot={getButtonSlot()}>
              <BenefitsStagebox.Title>{headline}</BenefitsStagebox.Title>
              <BenefitsStagebox.Subtitle>{subtitle}</BenefitsStagebox.Subtitle>
              <BenefitsStagebox.Details>
                {!hasFutureStartDate && endDate && (
                  <BenefitsStagebox.Detail icon={'user/clock'}>
                    Gültig bis{' '}
                    {formatDate(new Date(endDate), FormatDateType.Article)}
                  </BenefitsStagebox.Detail>
                )}
                {shouldShowLink && (
                  <BenefitsStagebox.Detail icon={'action/link'}>
                    <a href={link} target="_blank" rel="noreferrer">
                      {urlLabel ?? getDomainFromUrl(link)}
                    </a>
                  </BenefitsStagebox.Detail>
                )}
              </BenefitsStagebox.Details>
            </BenefitsStagebox>
          </HeroArea>
        </div>
        <Section>
          <Container>
            <Headline
              level={HeadlineLevel.Two}
              subText={teaserDescription}
              align={HeadlineAlign.Left}
              headlineColor={HeadlineColor.Gradient}
              className={styles.headline}
            >
              {teaserHeadline}
            </Headline>
          </Container>
        </Section>
        {details && (
          <Section>
            <TwoColumnContainer
              leftSideWidth={40}
              contents-left={
                <Headline
                  level={HeadlineLevel.Three}
                  subText="Alles Wichtige auf einen Blick"
                  align={HeadlineAlign.Left}
                  headlineColor={HeadlineColor.Gradient}
                >
                  Vorteil-Details
                </Headline>
              }
              contents-right={
                <UnorderedList className={styles.details} padding={false}>
                  {details?.map((detail) => (
                    <UnorderedListItem
                      key={detail}
                      className={styles.detail}
                      icon="action/checkmark"
                    >
                      <div {...getSafeInnerHTMLProp(detail)} />
                    </UnorderedListItem>
                  ))}
                </UnorderedList>
              }
            />
          </Section>
        )}
        <Section>
          <TwoColumnContainer
            leftSideWidth={40}
            contents-left={
              <Headline
                level={HeadlineLevel.Three}
                subText="Schritt für Schritt zu Ihrem enercity Vorteil"
                align={HeadlineAlign.Left}
                headlineColor={HeadlineColor.Gradient}
              >
                So lösen Sie Ihren Vorteil ein
              </Headline>
            }
            contents-right={
              <ol className={styles.redeemSteps}>
                {redeemStepItems.map((redeemStepItem) => (
                  <li
                    key={redeemStepItem}
                    className={styles.redeemStepsItem}
                    {...getSafeInnerHTMLProp(redeemStepItem)}
                  />
                ))}
              </ol>
            }
          />
        </Section>
        <Section>
          <Container>
            <ShowcaseElement
              title={showcaseTitle}
              description={showcaseDescription}
              image={{
                path: showcaseImage.path,
                name: showcaseImage.name,
                calculatedAspectRatio: 56.25,
              }}
              actionType={ClickableAction.None}
            />
          </Container>
        </Section>
        <Section>
          <TwoColumnContainer
            leftSideWidth={40}
            contents-left={
              <Headline
                level={HeadlineLevel.Three}
                subText="Häufig gestellte Fragen zu unseren<br/> Gutscheinen"
                align={HeadlineAlign.Left}
                headlineColor={HeadlineColor.Gradient}
              >
                Gut zu wissen
              </Headline>
            }
            contents-right={<Accordion items={faqItems} />}
          />
        </Section>
        <Section>
          <Container>
            {similarDeals.length > 0 && (
              <BenefitsSimilarDeals title="Wir haben noch mehr im Angebot">
                {similarDeals.map((deal) => (
                  <BenefitsDealCard
                    key={deal.link}
                    logo={deal.logo}
                    image={deal.image}
                    headline={deal.headline}
                    link={deal.link}
                    badgeText={deal.badgeText}
                  />
                ))}
              </BenefitsSimilarDeals>
            )}
          </Container>
        </Section>
        <Section>
          <Container>
            <div
              className={styles.footnote}
              {...getSafeInnerHTMLProp(footNote)}
            />
          </Container>
        </Section>
        <Spacer defaultSize={0} sm={6} />
      </div>
      <ErrorModal
        id={'not-available'}
        headline="Ups, Da ist etwas schief gelaufen"
        subText="Bitte versuchen Sie es später nochmal."
        isVisible={isNotAvailable}
        handleClose={() => setIsNotAvailable(false)}
        button={
          <Button
            align={ButtonAlign.Center}
            actionType={ClickableAction.Custom}
            onClick={() => setIsNotAvailable(false)}
          >
            Alles klar!
          </Button>
        }
      />

      <ErrorModal
        id={'not-eligible'}
        headline="Sorry! :( Leider nichts für Sie dabei."
        subText="Unsere Aktionen sind nur für aktive Strom- oder Gaskund:innen zu haben."
        isVisible={isNotEligible}
        handleClose={() => setIsNotEligible(false)}
        button={
          <Button
            align={ButtonAlign.Center}
            actionType={ClickableAction.Linkintern}
            linkintern="/privatkunden/produkte/strom-gas/tarife"
          >
            Jetzt Tarife ansehen
          </Button>
        }
      />

      <AppModal
        id="app-exclusive"
        handleClose={() => setShouldShowAppModal(false)}
        isVisible={shouldShowAppModal}
        text="Dieser Vorteil ist exklusiv in der enercity App verfügbar. Laden Sie sich die enercity App auf Ihr Smartphone herunter und profitieren Sie von diesem und weiteren exklusiven Vorteilen."
      />
    </React.Fragment>
  );
};

export default BenefitsPageContent;
